import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { User } from '../types'
import { apiService } from './api.service'

export interface UserListResponse {
  count: number
  next: string | null
  previous: string | null
  results: User[]
}

export interface UserCreateData {
  username: string
  email: string
  password?: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  groups: number[]
  organizations?: number[]
  default_organization_id?: number | null
  organization_roles?: Record<string, number[]>
  user_permissions: string[]
}

export interface UserUpdateData extends Partial<UserCreateData> {
  id: number
}

class UserService {
  private _listAllCache: Promise<UserListResponse> | null = null

  async listAll(params?: { page?: number; page_size?: number; full_perms?: boolean; [key: string]: any }): Promise<UserListResponse> {
    if (!params || Object.keys(params).length === 0) {
      if (!this._listAllCache) {
        const queryParams = new URLSearchParams()
        queryParams.append('page', '1')
        queryParams.append('page_size', '100000')
        this._listAllCache = apiService.get<UserListResponse>(`users/?${queryParams.toString()}`).then((response) => response.data)
      }
      return this._listAllCache
    } else {
      const mergedParams: { [key: string]: any } = { page: 1, page_size: 100000, ...params }
      const queryParams = new URLSearchParams()
      Object.keys(mergedParams).forEach((key) => {
        const value = mergedParams[key]
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString())
        }
      })

      const response = await apiService.get<UserListResponse>(`users/?${queryParams.toString()}`)
      return response.data
    }
  }

  clearCache(): void {
    this._listAllCache = null
  }

  // Alias for compatibility
  async getUsers(params?: { page?: number; page_size?: number; full_perms?: boolean }): Promise<UserListResponse> {
    return this.listAll(params)
  }

  async getById(id: number): Promise<User> {
    const response = await apiService.get<User>(`users/${id}/`)
    return response.data
  }

  async create(userData: UserCreateData): Promise<User> {
    const response = await apiService.post<User>('users/', userData)
    return response.data
  }

  async update(id: number, userData: Partial<UserCreateData>): Promise<User> {
    const response = await apiService.patch<User>(`users/${id}/`, userData)
    return response.data
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`users/${id}/`)
  }

  async deactivateTotp(user: User): Promise<boolean> {
    try {
      await apiService.post(`users/${user.id}/deactivate_totp/`)
      return true
    } catch (error) {
      return false
    }
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    includeDocumentCount?: boolean,
    extraParams?: any
  ): Promise<UserListResponse> {
    const params: any = {
      page,
      page_size: extraParams?.page_size ?? DEFAULT_PAGE_SIZE,
      full_perms: true,
    }

    if (sortField) {
      const prefix = sortReverse ? '-' : ''
      params.ordering = `${prefix}${sortField}`
    }

    if (nameFilter) {
      params.username__icontains = nameFilter
    }

    if (includeDocumentCount) {
      params.include_document_count = true
    }

    if (extraParams) {
      Object.assign(params, extraParams)
    }

    return this.listAll(params)
  }
}

export const userService = new UserService()
